import React, { useEffect, useMemo, useState } from "react";
import { format, startOfMonth } from "date-fns";
import {
  Area,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import PageContainer from "@/components/PageContainer";
import InnerContainerLoader from "@/components/InnerContainerLoader";
import AdminDashboardService from "@/services/AdminDashboardService";
import useStore from "@/store";
import { THEME, accuracyToColor } from "@/lib/theme";
import { LEARNING_LANGUAGES } from "@/constants/langcraftLanguages";
import { FOUNDER_EMAILS } from "@/constants/founders";

const OWNER_EMAILS = FOUNDER_EMAILS;
const RANGE_OPTIONS = [
  { label: "7 days", value: 7 },
  { label: "30 days", value: 30 },
  { label: "1 year", value: 365 },
  { label: "All time", value: null },
];

const normalizeOwnerEmail = (email) => {
  if (!email) {
    return "";
  }
  let value = email.trim().toLowerCase();
  if (value.endsWith("@gmail.com")) {
    const [local, domain] = value.split("@");
    const normalizedLocal = local.split("+")[0].replace(/\./g, "");
    return `${normalizedLocal}@${domain}`;
  }
  return value;
};

const NORMALIZED_OWNER_EMAILS = OWNER_EMAILS.map(normalizeOwnerEmail);

const formatNumber = (value, options = {}) => {
  if (value === undefined || value === null) {
    return "—";
  }
  return new Intl.NumberFormat("en-US", options).format(value);
};

// "1h 4m", "3m 20s", "45s"
const formatDuration = (seconds) => {
  const total = Math.round(Number(seconds) || 0);
  if (total < 60) return `${total}s`;
  const mins = Math.floor(total / 60);
  if (mins < 60) return `${mins}m ${total % 60}s`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

// The copilot answers in light markdown; show **bold** as bold.
const renderBold = (text) =>
  String(text)
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, index) =>
      part.startsWith("**") && part.endsWith("**") ? (
        <strong key={index} className="text-primary">
          {part.slice(2, -2)}
        </strong>
      ) : (
        part
      )
    );

const languageName = (code = "") => {
  const match = LEARNING_LANGUAGES.find(
    (language) => language.code === code.toLowerCase().split(/[-_]/)[0]
  );
  return match ? `${match.name}${code.includes("-") ? ` (${code})` : ""}` : code || "Unknown";
};

const formatPercent = (value) => {
  if (value === undefined || value === null) {
    return "—";
  }
  return `${Math.round(value)}%`;
};

const FounderDashboard = () => {
  const { user } = useStore().user;
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [question, setQuestion] = useState("");
  const [qaHistory, setQaHistory] = useState([]);
  const [asking, setAsking] = useState(false);
  const [rangeDays, setRangeDays] = useState(30);

  const ownerEmail = normalizeOwnerEmail(user?.email || "");
  const isOwner = ownerEmail && NORMALIZED_OWNER_EMAILS.includes(ownerEmail);

  useEffect(() => {
    if (!isOwner) {
      setLoading(false);
      return;
    }
    const fetchSnapshot = async () => {
      try {
        setLoading(true);
        const data = await AdminDashboardService.getOverview(rangeDays);
        setSnapshot(data);
      } catch (err) {
        console.error("Failed to load founder dashboard snapshot:", err);
        setError("We could not load the dashboard data.");
      } finally {
        setLoading(false);
      }
    };
    fetchSnapshot();
  }, [isOwner, rangeDays]);

  const summary = snapshot?.summary || {};
  const rangeSummary = snapshot?.range_summary || {};
  const languageBreakdown = snapshot?.language_breakdown || [];
  const emailDomainBreakdown = snapshot?.email_domain_breakdown || [];
  const dailyActivity = snapshot?.daily_activity || [];
  const userInsights = snapshot?.user_insights || [];

  // Daily points for 7/30 days, monthly for longer ranges. Accuracy is the
  // session-weighted average for each bucket.
  const chartData = useMemo(() => {
    const aggregates = new Map();
    const monthly = rangeDays === null || rangeDays === 365;

    dailyActivity.forEach((point) => {
      const dateObj = new Date(point.date);
      if (Number.isNaN(dateObj.getTime())) {
        return;
      }
      const bucketStart = monthly ? startOfMonth(dateObj) : dateObj;
      const groupKey = monthly
        ? `${bucketStart.getFullYear()}-${bucketStart.getMonth()}`
        : point.date;
      const existing = aggregates.get(groupKey) || {
        label: format(bucketStart, monthly ? "MMM yy" : "MMM d"),
        active_users: 0,
        sessions: 0,
        accuracyWeight: 0,
        accuracySum: 0,
        sortValue: bucketStart,
      };
      existing.active_users += point.active_users || 0;
      existing.sessions += point.sessions || 0;
      if (typeof point.avg_accuracy === "number" && point.sessions) {
        existing.accuracySum += point.avg_accuracy * point.sessions;
        existing.accuracyWeight += point.sessions;
      }
      aggregates.set(groupKey, existing);
    });

    return Array.from(aggregates.values())
      .sort((a, b) => a.sortValue - b.sortValue)
      .map(({ sortValue, accuracySum, accuracyWeight, ...rest }) => ({
        ...rest,
        avg_accuracy: accuracyWeight ? Math.round(accuracySum / accuracyWeight) : null,
      }));
  }, [dailyActivity, rangeDays]);

  const customerMixData = useMemo(() => {
    const mix = snapshot?.customer_mix || {};
    const newCount =
      mix.new_users ??
      summary.one_time_users ??
      summary.new_users_last_7_days ??
      0;
    const returningCount =
      mix.returning_users ?? summary.returning_users ?? 0;
    return [
      {
        label: "New customers",
        value: newCount,
        color: THEME.primary,
      },
      {
        label: "Returning customers",
        value: returningCount,
        color: THEME.accent,
      },
    ];
  }, [
    snapshot?.customer_mix?.new_users,
    snapshot?.customer_mix?.returning_users,
    summary.one_time_users,
    summary.new_users_last_7_days,
    summary.returning_users,
  ]);

  const handleAsk = async () => {
    if (!question.trim() || asking) {
      return;
    }
    setAsking(true);
    try {
      const trimmed = question.trim();
      const result = await AdminDashboardService.askQuestion(trimmed);
      setQaHistory((prev) => [
        {
          id: Date.now(),
          question: trimmed,
          answer: result?.answer,
          ai_available: result?.ai_available,
          error: result?.error,
        },
        ...prev,
      ]);
      setQuestion("");
    } catch (err) {
      console.error("Failed to ask dashboard question:", err);
      setQaHistory((prev) => [
        {
          id: Date.now(),
          question,
          answer: null,
          ai_available: false,
          error: "We could not process that question. Please try again.",
        },
        ...prev,
      ]);
    } finally {
      setAsking(false);
    }
  };

  const renderSummaryCards = () => {
    const activeUsers = rangeSummary.unique_users ?? summary.total_users;
    const newUsersRange = rangeSummary.new_users ?? summary.new_users_last_7_days ?? 0;
    const sessionsRange = rangeSummary.practice_sessions ?? summary.total_practice_sessions ?? 0;
    const avgAccuracyRange = rangeSummary.avg_accuracy ?? summary.average_accuracy_last_7_days;
    const avgDurationRange = rangeSummary.avg_duration ?? summary.average_duration_per_session;
    const collectionsRange = rangeSummary.collections ?? summary.total_collections;
    const returningRange = rangeSummary.returning_users ?? summary.returning_users ?? 0;
    const oneTimeRange = rangeSummary.one_time_users ?? summary.one_time_users ?? 0;
    const retentionRate =
      returningRange + oneTimeRange > 0
        ? (returningRange / (returningRange + oneTimeRange)) * 100
        : null;
    const sessionsPerUser = activeUsers ? sessionsRange / activeUsers : null;

    const primary = [
      { label: "Active users", value: formatNumber(activeUsers), hint: "Practised at least once" },
      { label: "New users", value: formatNumber(newUsersRange), hint: "Signed up in this range" },
      {
        label: "Practice sessions",
        value: formatNumber(sessionsRange),
        hint:
          sessionsPerUser !== null
            ? `${formatNumber(sessionsPerUser, { maximumFractionDigits: 1 })} per active user`
            : "",
      },
      {
        label: "Avg accuracy",
        value: formatPercent(avgAccuracyRange),
        hint: "Pronunciation, all sessions",
        color:
          typeof avgAccuracyRange === "number" ? accuracyToColor(avgAccuracyRange) : undefined,
      },
    ];
    const secondary = [
      {
        label: "Avg session",
        value:
          avgDurationRange !== null && avgDurationRange !== undefined
            ? formatDuration(avgDurationRange)
            : "—",
      },
      { label: "Retention", value: formatPercent(retentionRate) },
      { label: "Collections created", value: formatNumber(collectionsRange) },
      { label: "Total users", value: formatNumber(summary.total_users) },
    ];

    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {primary.map((card) => (
            <div key={card.label} className="rounded-xl border border-line bg-surface p-4 sm:p-5">
              <p className="text-sm text-fg-muted">{card.label}</p>
              <p
                className="mt-2 font-display text-3xl font-bold leading-none text-fg sm:text-4xl"
                style={card.color ? { color: card.color } : undefined}
              >
                {card.value}
              </p>
              {card.hint && <p className="mt-2 text-xs text-fg-subtle">{card.hint}</p>}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {secondary.map((card) => (
            <div
              key={card.label}
              className="flex flex-col gap-0.5 rounded-lg border border-line bg-surface/60 px-4 py-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-2"
            >
              <span className="text-xs text-fg-muted">{card.label}</span>
              <span className="whitespace-nowrap font-display text-lg font-semibold text-fg">
                {card.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderRetentionCard = () => {
    const returning = customerMixData[1]?.value || 0;
    const fresh = customerMixData[0]?.value || 0;
    const total = returning + fresh;
    const returningShare = total ? Math.round((returning / total) * 100) : 0;
    return (
      <div className="flex flex-col rounded-xl border border-line bg-surface p-4 sm:p-5">
        <h3 className="text-base font-semibold text-fg">Retention</h3>
        <p className="text-xs text-fg-subtle">Learners who came back after their first session</p>

        <div className="mt-5">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-fg-muted">Came back for another session</span>
            <span className="font-display text-2xl font-bold text-accent">{returningShare}%</span>
          </div>
          <div className="mt-2 flex h-2.5 overflow-hidden rounded-full bg-line">
            <div className="bg-accent" style={{ width: `${returningShare}%` }} />
            <div className="bg-primary/70" style={{ width: `${100 - returningShare}%` }} />
          </div>
          <div className="mt-2 flex justify-between text-xs text-fg-muted">
            <span>
              <span className="mr-1 inline-block h-2 w-2 rounded-full bg-accent" />
              Returning {formatNumber(returning)}
            </span>
            <span>
              <span className="mr-1 inline-block h-2 w-2 rounded-full bg-primary/70" />
              One-time {formatNumber(fresh)}
            </span>
          </div>
        </div>

      </div>
    );
  };

  const getLanguageFlag = (languageName = "") => {
    const normalized = languageName.toLowerCase();
    const code = normalized.split(/[-_]/)[0];
    const flagMap = {
      english: "🇺🇸",
      spanish: "🇪🇸",
      portuguese: "🇵🇹",
      french: "🇫🇷",
      german: "🇩🇪",
      italian: "🇮🇹",
      chinese: "🇨🇳",
      mandarin: "🇨🇳",
      japanese: "🇯🇵",
      korean: "🇰🇷",
      hindi: "🇮🇳",
      arabic: "🇦🇪",
      russian: "🇷🇺",
      turkish: "🇹🇷",
      dutch: "🇳🇱",
      swedish: "🇸🇪",
      "en-us": "🇺🇸",
      "en-gb": "🇬🇧",
      "pt-br": "🇧🇷",
      "fr-be": "🇧🇪",
      en: "🇺🇸",
      es: "🇪🇸",
      pt: "🇵🇹",
      fr: "🇫🇷",
      de: "🇩🇪",
      hi: "🇮🇳",
      zh: "🇨🇳",
      ja: "🇯🇵",
      ko: "🇰🇷",
    };
    return (
      flagMap[normalized] ||
      flagMap[code] ||
      LEARNING_LANGUAGES.find((language) => language.code === code)?.flag ||
      "🏳️"
    );
  };

  const renderLanguageBreakdown = () => {
    return (
      <div className="rounded-xl border border-line bg-surface p-4 sm:p-5">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-fg">Top languages</h3>
        <span className="text-xs text-fg-muted">Users · Sessions · Accuracy</span>
      </div>
      <div className="mt-4 divide-y divide-line max-h-[20rem] overflow-y-auto pr-2">
        {languageBreakdown.length === 0 && (
          <p className="py-3 text-sm text-fg-muted">
            Practice data will appear after the first sessions are logged.
          </p>
        )}
        {languageBreakdown.map((language) => (
          <div
            key={language.language}
            className="flex items-center justify-between gap-4 py-3"
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl" aria-hidden>{getLanguageFlag(language.language)}</span>
              <div>
                <p className="text-sm font-semibold text-fg">
                  {languageName(language.language)}
                </p>
                <p className="text-xs text-fg-muted">
                  {formatNumber(
                    language.users ??
                      language.unique_users ??
                      language.user_count ??
                      0
                  )}{" "}
                  users · {formatNumber(language.sessions)} sessions
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden w-24 rounded-full bg-line sm:block">
                <div
                  className="h-1.5 rounded-full"
                  style={{
                    width: `${Math.min(language.avg_accuracy || 0, 100)}%`,
                    backgroundColor: accuracyToColor(language.avg_accuracy || 0),
                  }}
                />
              </div>
              <p
                className="w-12 text-right text-base font-semibold"
                style={{ color: accuracyToColor(language.avg_accuracy || 0) }}
              >
                {formatPercent(language.avg_accuracy)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
    );
  };

  const renderEmailDomainBreakdown = () => {
    const total = emailDomainBreakdown.reduce(
      (acc, item) => acc + (item.users || 0),
      0
    );
    return (
      <div className="rounded-xl border border-line bg-surface p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-fg">Email providers</h3>
          <span className="text-xs text-fg-muted">Share of users</span>
        </div>
        <div className="mt-4 divide-y divide-line max-h-[20rem] overflow-y-auto pr-2">
          {emailDomainBreakdown.length === 0 && (
            <p className="py-3 text-sm text-fg-muted">
              We’ll populate this once signups begin.
            </p>
          )}
          {emailDomainBreakdown.map((domain) => {
            const percentage = total
              ? Math.round(((domain.users || 0) / total) * 100)
              : 0;
            return (
              <div key={domain.domain} className="flex items-center justify-between py-3 text-sm">
                <div>
                  <p className="font-semibold text-sm text-fg">
                    {domain.domain || "unknown"}
                  </p>
                  <p className="text-xs text-fg-muted">
                    {formatNumber(domain.users || 0)} users
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-24 rounded-full bg-line">
                    <div
                      className="h-1.5 rounded-full bg-fg-muted"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-fg">
                    {percentage}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderUserInsights = () => (
    <div className="rounded-xl border border-line bg-surface p-4 sm:p-5">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-fg">All users</h3>
        <span className="text-xs text-fg-muted">
          {formatNumber(userInsights.length)} total
        </span>
      </div>
      <div className="mt-4 overflow-x-auto">
        <div className="max-h-[28rem] overflow-y-auto">
          <table className="min-w-[1000px] text-xs text-fg">
            <thead>
              <tr className="sticky top-0 bg-surface text-left font-mono text-[11px] uppercase tracking-wider text-fg-muted">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Email domain</th>
                <th className="py-2 pr-4">User type</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Joined</th>
                <th className="py-2 pr-4">Practice sessions</th>
                <th className="py-2 pr-4">Collections</th>
                <th className="py-2 pr-4">Videos</th>
                <th className="py-2 pr-4">Languages</th>
              </tr>
            </thead>
            <tbody>
              {userInsights.map((user) => (
                <tr
                  key={user.id}
                  className="border-t border-line text-[13px] text-fg"
                >
                  <td className="py-3 pr-4 font-medium">
                    {user.display_name || user.email || "—"}
                  </td>
                  <td className="py-3 pr-4">{user.email_domain || "—"}</td>
                  <td className="py-3 pr-4 capitalize">{user.user_type}</td>
                  <td className="py-3 pr-4">{user.status || "—"}</td>
                  <td className="py-3 pr-4">
                    {user.joined_at
                      ? format(new Date(user.joined_at), "MMM d, yyyy")
                      : "—"}
                  </td>
                  <td className="py-3 pr-4">
                    {formatNumber(user.practice_sessions)}
                  </td>
                  <td className="py-3 pr-4">{formatNumber(user.collections)}</td>
                  <td className="py-3 pr-4">{formatNumber(user.videos)}</td>
                  <td className="py-3 pr-4">{formatNumber(user.languages)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {userInsights.length === 0 && (
            <div className="py-8 text-center text-fg-muted">
              No user data yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (!isOwner) {
    return (
      <PageContainer>
        <div className="mx-auto max-w-3xl rounded-2xl border border-line bg-canvas/40 p-10 text-center text-fg">
          <h2 className="text-2xl font-semibold">Admin dashboard</h2>
          <p className="mt-4 text-sm text-fg-muted">
            This page is available only to the SpeechCraft founders.
          </p>
        </div>
      </PageContainer>
    );
  }

  const chartTooltipStyle = {
    backgroundColor: THEME.elevated,
    borderRadius: "0.375rem",
    border: `1px solid ${THEME.line}`,
    color: THEME.fg,
    fontSize: "12px",
  };

  return (
    <PageContainer>
      {/* min-w-0 keeps the wide users table scrolling inside its card on phones */}
      <div className="w-full min-w-0 space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="hud-label !text-primary">Private · founders only</p>
            <h1 className="mt-2 font-display text-3xl font-bold text-fg">Founder dashboard</h1>
            <p className="mt-1 max-w-xl text-sm text-fg-muted">
              Usage and performance across all learners. Team accounts are left out of
              these numbers.
            </p>
          </div>
          <div className="flex w-fit gap-1 rounded-full border border-line bg-surface p-1">
            {RANGE_OPTIONS.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => setRangeDays(option.value)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition sm:px-4 ${
                  rangeDays === option.value
                    ? "bg-primary text-primary-fg"
                    : "text-fg-muted hover:text-fg"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {loading && <InnerContainerLoader />}
        {!loading && error && (
          <div className="rounded-xl border border-danger/30 bg-danger/10 p-4 text-danger">
            {error}
          </div>
        )}

        {!loading && !error && snapshot && (
          <>
            {renderSummaryCards()}

            <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
              <div className="rounded-xl border border-line bg-surface p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-base font-semibold text-fg">Activity</h3>
                  <div className="flex gap-4 text-xs text-fg-muted">
                    <span className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-primary" /> Sessions
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="h-0.5 w-3 bg-accent" /> Active users
                    </span>
                  </div>
                </div>
                <div className="mt-4 h-56 sm:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ left: -20, right: 4, top: 4 }}>
                      <XAxis
                        dataKey="label"
                        stroke={THEME.fgMuted}
                        tickLine={false}
                        axisLine={false}
                        style={{ fontSize: "11px" }}
                        minTickGap={16}
                      />
                      <YAxis
                        stroke={THEME.fgMuted}
                        tickLine={false}
                        axisLine={false}
                        style={{ fontSize: "11px" }}
                        allowDecimals={false}
                      />
                      <RechartsTooltip
                        contentStyle={chartTooltipStyle}
                        labelStyle={{ color: THEME.fgMuted }}
                        cursor={{ stroke: THEME.line }}
                      />
                      <Area
                        type="monotone"
                        dataKey="sessions"
                        fill="rgb(57 255 136 / 0.18)"
                        stroke={THEME.primary}
                        strokeWidth={2}
                        name="Sessions"
                      />
                      <Line
                        type="monotone"
                        dataKey="active_users"
                        stroke={THEME.accent}
                        strokeWidth={2}
                        dot={false}
                        name="Active users"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
              {renderRetentionCard()}
            </div>

            <div className="rounded-xl border border-line bg-surface p-4 sm:p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-base font-semibold text-fg">Pronunciation accuracy</h3>
                  <p className="text-xs text-fg-subtle">
                    Average score per {rangeDays === null || rangeDays === 365 ? "month" : "day"},
                    weighted by sessions
                  </p>
                </div>
              </div>
              <div className="mt-4 h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ left: -20, right: 4, top: 4 }}>
                    <XAxis
                      dataKey="label"
                      stroke={THEME.fgMuted}
                      tickLine={false}
                      axisLine={false}
                      style={{ fontSize: "11px" }}
                      minTickGap={16}
                    />
                    <YAxis
                      domain={[0, 100]}
                      stroke={THEME.fgMuted}
                      tickLine={false}
                      axisLine={false}
                      style={{ fontSize: "11px" }}
                    />
                    <RechartsTooltip
                      contentStyle={chartTooltipStyle}
                      labelStyle={{ color: THEME.fgMuted }}
                      cursor={{ stroke: THEME.line }}
                      formatter={(value) => [value === null ? "—" : `${value}%`, "Accuracy"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="avg_accuracy"
                      stroke={THEME.primary}
                      strokeWidth={2}
                      dot={{ r: 2, fill: THEME.primary }}
                      connectNulls
                      name="Accuracy"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              {renderLanguageBreakdown()}
              {renderEmailDomainBreakdown()}
            </div>

            {renderUserInsights()}

            <div className="rounded-xl border border-line bg-surface p-4 sm:p-6">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h3 className="text-base font-semibold text-fg">Ask about user behaviour</h3>
                  <p className="text-sm text-fg-muted">
                    We’ll combine the latest metrics with your question and route
                    it to the analytics copilot.
                  </p>
                </div>
              </div>
              <div className="mt-4 space-y-3">
                <textarea
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                  placeholder="e.g., How many active learners did we have last week vs. this week?"
                  className="w-full rounded-lg border border-line bg-elevated p-4 text-sm text-fg placeholder:text-fg-subtle focus:border-primary/60 focus:outline-none"
                  rows={3}
                />
                <button
                  type="button"
                  onClick={handleAsk}
                  disabled={asking || !question.trim()}
                  className="inline-flex items-center justify-center rounded bg-primary px-6 py-2 text-sm font-semibold text-primary-fg transition hover:bg-primary-strong disabled:opacity-50"
                >
                  {asking ? "Thinking..." : "Ask"}
                </button>
              </div>
              {qaHistory.length > 0 && (
                <div className="mt-6 space-y-4">
                  {qaHistory.map((entry) => (
                    <div
                      key={entry.id}
                      className="rounded-2xl border border-line bg-surface/80 p-4"
                    >
                      <p className="text-xs uppercase tracking-[0.3em] text-fg-muted">
                        You asked
                      </p>
                      <p className="mt-1 text-base text-fg">{entry.question}</p>
                      <p className="mt-3 text-xs uppercase tracking-[0.3em] text-fg-muted">
                        Copilot
                      </p>
                      <p className="mt-1 whitespace-pre-line text-sm leading-6 text-fg">
                        {renderBold(
                          entry.answer ||
                            entry.error ||
                            "No response returned. Please try again."
                        )}
                      </p>
                      {!entry.ai_available && (
                        <p className="mt-2 text-xs text-fg-muted">
                          AI answers are disabled right now.
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default FounderDashboard;
